import { AdminHeader } from "@/components/AdminHeader";
import ApiStatus from "@/pages/admin/ApiStatus";
import { Link } from "react-router-dom";
import FraudMap from "@/pages/admin/FraudMap";
import SearchPerfCard from "@/pages/admin/SearchPerfCard";
import { useEffect, useMemo, useState } from "react";
import { api, errMsg, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { CalendarCheck, Clock, Car, DollarSign, Megaphone, Wrench, CalendarX2, TrendingUp, TrendingDown } from "lucide-react";

const CARD = "bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl p-6";

export default function OverviewTab() {
  const [stats, setStats] = useState(null);
  const [maintenance, setMaintenance] = useState([]);
  const [blackouts, setBlackouts] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [payments, setPayments] = useState([]);
  const [promo, setPromo] = useState({ enabled: false, message: "" });
  const [savingPromo, setSavingPromo] = useState(false);
  const [fleet, setFleet] = useState(null);
  const [couponPerf, setCouponPerf] = useState([]);
  const [recovery, setRecovery] = useState(null);
  const [recoveryRuns, setRecoveryRuns] = useState([]);
  const [nudgeDelay, setNudgeDelay] = useState(2);
  const [util, setUtil] = useState(null);

  const saveNudgeDelay = async (h) => {
    try {
      await api.put("/admin/settings/recovery", { nudge_delay_hours: h });
      setNudgeDelay(h);
      toast.success(`Recovery nudge timing set to ${h} hour${h === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const savePromo = async () => {
    setSavingPromo(true);
    try {
      await api.put("/admin/promo", promo);
      toast.success("Promotion banner updated");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSavingPromo(false);
    }
  };

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/maintenance").then((r) => setMaintenance(r.data)).catch(() => {});
    api.get("/admin/blackouts").then((r) => setBlackouts(r.data)).catch(() => {});
    api.get("/admin/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
    api.get("/admin/payments").then((r) => setPayments(r.data)).catch(() => {});
    api.get("/promo").then((r) => setPromo(r.data)).catch(() => {});
    api.get("/admin/fleet-board").then((r) => setFleet(r.data)).catch(() => {});
    api.get("/admin/coupon-performance").then((r) => { setCouponPerf(r.data.codes || []); setRecovery(r.data.recovery || null); }).catch(() => {});
    api.get("/admin/recovery-runs").then((r) => setRecoveryRuns(r.data)).catch(() => {});
    api.get("/admin/settings/recovery").then((r) => setNudgeDelay(r.data.nudge_delay_hours || 2)).catch(() => {});
    api.get("/admin/utilization").then((r) => setUtil(r.data)).catch(() => {});
  }, []);

  const months = useMemo(() => {
    const out = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      out.push({
        key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
        label: d.toLocaleDateString("en-US", { month: "short" }),
        total: 0,
      });
    }
    const byKey = Object.fromEntries(out.map((m) => [m.key, m]));
    maintenance.forEach((r) => {
      const k = (r.date || "").slice(0, 7);
      if (byKey[k]) byKey[k].total += r.cost || 0;
    });
    return out;
  }, [maintenance]);

  const revenue = useMemo(() => {
    const now = new Date();
    const curKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const prevKey = `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, "0")}`;
    let thisMonth = 0, lastMonth = 0;
    payments.forEach((p) => {
      if (p.refunded) return;
      const k = (p.created_at || "").slice(0, 7);
      if (k === curKey) thisMonth += p.amount || 0;
      else if (k === prevKey) lastMonth += p.amount || 0;
    });
    const delta = lastMonth > 0 ? ((thisMonth - lastMonth) / lastMonth) * 100 : null;
    return { thisMonth, lastMonth, delta };
  }, [payments]);

  const downtime = useMemo(() => {
    const now = new Date();
    const mStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const mEnd = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const DAY = 86400000;
    const active = vehicles.filter((v) => v.active !== false);
    const fleetRate = active.reduce((s, v) => s + (v.price_per_day || 0), 0);
    let vehicleDays = 0, lost = 0;
    blackouts.forEach((b) => {
      const s = new Date(Math.max(new Date(b.start_date + "T00:00:00").getTime(), mStart.getTime()));
      const e = new Date(Math.min(new Date(b.end_date + "T00:00:00").getTime(), mEnd.getTime()));
      const days = Math.round((e - s) / DAY);
      if (days <= 0) return;
      if (b.vehicle_id) {
        vehicleDays += days;
        lost += days * (vehicles.find((v) => v.id === b.vehicle_id)?.price_per_day || 0);
      } else {
        vehicleDays += days * (active.length || 1);
        lost += days * fleetRate;
      }
    });
    return { vehicleDays, lost };
  }, [blackouts, vehicles]);

  if (!stats) return <p className="text-[#4A4A4A] font-ui">Loading stats…</p>;

  const maxSpend = Math.max(...months.map((m) => m.total), 1);
  const sixMonthTotal = months.reduce((s, m) => s + m.total, 0);
  const monthName = new Date().toLocaleDateString("en-US", { month: "long" });

  const cards = [
    { icon: CalendarCheck, label: "Confirmed Bookings", value: stats.confirmed_bookings, testid: "stats-confirmed" },
    { icon: Clock, label: "Pending Payment", value: stats.pending_bookings, testid: "stats-pending" },
    { icon: DollarSign, label: "Revenue (Confirmed)", value: fmtMoney(stats.revenue), testid: "stats-revenue" },
    { icon: Car, label: "Active Vehicles", value: stats.active_vehicles, testid: "stats-vehicles" },
  ];

  return (
    <div data-testid="admin-overview">
      <AdminHeader testid="admin-header-overview" eyebrow="Mission Control" title="Overview" description="Your rental business at a glance." actions={<ApiStatus />} />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {cards.map(({ icon: Icon, label, value, testid }) => (
        <div key={testid} data-testid={testid} className={`${CARD} hover:-translate-y-0.5 transition-transform duration-300`}>
          <div className="flex items-center justify-between">
            <p className="font-ui text-xs tracking-[0.15em] uppercase text-[#4A4A4A]">{label}</p>
            <div className="h-9 w-9 rounded-full bg-[#C71E2E]/10 flex items-center justify-center shrink-0">
              <Icon size={16} className="text-[#C71E2E]" />
            </div>
          </div>
          <p className="mt-3 font-ui text-3xl font-semibold tracking-tight text-[#1A1A1A]">{value}</p>
        </div>
      ))}
      </div>

      {fleet && (
        <div data-testid="fleet-board-card" className={`${CARD} mt-6`}>
          <div className="flex items-center justify-between">
            <p className="font-ui text-xs tracking-[0.2em] uppercase text-[#4A4A4A] flex items-center gap-2">
              <Car size={14} className="text-[#C71E2E]" /> Fleet Board — {fleet.date}
            </p>
            <Link to="/admin?tab=bookings" data-testid="fleet-board-open" className="text-xs font-ui text-[#C71E2E] hover:underline">Open Bookings →</Link>
          </div>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Link to="/admin?tab=bookings" data-testid="fleet-out" className="rounded-xl border border-[#E5DED5] p-4 hover:border-[#C71E2E]/40 hover:-translate-y-0.5 transition-all duration-200 block">
              <p className="font-ui text-[10px] tracking-[0.15em] uppercase text-[#4A4A4A]">Vehicles out</p>
              <p className="mt-1 font-ui text-2xl font-semibold text-[#1A1A1A]">{fleet.out_now}</p>
            </Link>
            <Link to="/admin?tab=bookings" data-testid="fleet-due" className="rounded-xl border border-[#E5DED5] p-4 hover:border-[#C71E2E]/40 hover:-translate-y-0.5 transition-all duration-200 block">
              <p className="font-ui text-[10px] tracking-[0.15em] uppercase text-[#4A4A4A]">Due back today</p>
              <p className="mt-1 font-ui text-2xl font-semibold text-[#1A1A1A]">{fleet.due_today.length}</p>
            </Link>
            <Link to="/admin?tab=bookings" data-testid="fleet-overdue" className={`rounded-xl border p-4 hover:-translate-y-0.5 transition-all duration-200 block ${fleet.overdue.length ? "border-red-300 bg-red-50/60 hover:border-red-400" : "border-[#E5DED5] hover:border-[#C71E2E]/40"}`}>
              <p className="font-ui text-[10px] tracking-[0.15em] uppercase text-[#4A4A4A]">Overdue check-ins</p>
              <p className={`mt-1 font-ui text-2xl font-semibold ${fleet.overdue.length ? "text-red-700" : "text-[#1A1A1A]"}`}>{fleet.overdue.length}</p>
            </Link>
          </div>
          {(fleet.overdue.length > 0 || fleet.due_today.length > 0) && (
            <div data-testid="fleet-board-list" className="mt-4">
              {fleet.overdue.map((b) => (
                <Link key={b.id} to={`/admin?tab=bookings&booking=${b.id}`} data-testid={`fleet-overdue-row-${b.id}`} className="flex items-center justify-between text-sm font-ui text-[#1A1A1A] hover:text-[#C71E2E] border-b border-[#F5EFE6] py-2 transition-colors">
                  <span>{b.vehicle_name} — {b.customer_name}</span>
                  <span className="text-xs text-red-600 font-semibold">overdue since {b.end_date}</span>
                </Link>
              ))}
              {fleet.due_today.map((b) => (
                <Link key={b.id} to={`/admin?tab=bookings&booking=${b.id}`} data-testid={`fleet-due-row-${b.id}`} className="flex items-center justify-between text-sm font-ui text-[#1A1A1A] hover:text-[#C71E2E] border-b border-[#F5EFE6] py-2 transition-colors">
                  <span>{b.vehicle_name} — {b.customer_name}</span>
                  <span className="text-xs text-amber-600">due back today</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      <div data-testid="coupon-performance-card" className={`${CARD} mt-6`}>
        <p className="font-ui text-xs tracking-[0.2em] uppercase text-[#4A4A4A]">Coupon Performance — All Time</p>
        {couponPerf.length === 0 ? (
          <p className="mt-3 text-xs font-ui text-muted-foreground">No code redemptions yet.</p>
        ) : (
          <div className="mt-4 space-y-2.5">
            {couponPerf.map((c) => (
              <div key={c.code} data-testid={`coupon-perf-${c.code}`} className="flex items-center justify-between gap-3 text-sm font-ui border-b border-[#F5EFE6] pb-2 last:border-0">
                <span className="font-semibold text-[#1A1A1A]">{c.code}</span>
                <span className="text-xs text-muted-foreground">{c.redemptions} redemption{c.redemptions === 1 ? "" : "s"}</span>
                <span className="font-semibold text-[#1A1A1A]">${Number(c.revenue).toFixed(2)}</span>
              </div>
            ))}
          </div>
        )}
        {recovery && recovery.nudged > 0 && (
          <div data-testid="recovery-summary" className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
            <p className="font-ui text-[10px] tracking-[0.25em] uppercase text-emerald-800">Abandoned-booking recovery — {recovery.code}</p>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 font-ui">
              <div>
                <p data-testid="recovery-nudged" className="text-lg font-semibold text-[#1A1A1A]">{recovery.nudged}</p>
                <p className="text-xs text-muted-foreground">nudged</p>
              </div>
              <div>
                <p data-testid="recovery-recovered" className="text-lg font-semibold text-emerald-700">{recovery.recovered}</p>
                <p className="text-xs text-muted-foreground">recovered &amp; paid</p>
              </div>
              <div>
                <p data-testid="recovery-rate" className="text-lg font-semibold text-[#1A1A1A]">{recovery.rate_pct}%</p>
                <p className="text-xs text-muted-foreground">recovery rate</p>
              </div>
              <div>
                <p data-testid="recovery-revenue" className="text-lg font-semibold text-[#1A1A1A]">${Number(recovery.recovered_revenue).toFixed(2)}</p>
                <p className="text-xs text-muted-foreground">recovered revenue</p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div data-testid="recovery-runs-card" className={`${CARD} mt-6`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="font-ui text-xs tracking-[0.2em] uppercase text-[#4A4A4A]">Recovery Runs — Recent</p>
          <div className="flex items-center gap-2">
            <span className="text-xs font-ui text-muted-foreground">Nudge after</span>
            {[1, 2, 3].map((h) => (
              <button key={h} data-testid={`nudge-delay-${h}`} onClick={() => saveNudgeDelay(h)} className={`px-3 py-1 rounded-full text-xs font-ui border transition-colors ${nudgeDelay === h ? "bg-[#C71E2E] text-white border-[#C71E2E]" : "bg-white text-[#4A4A4A] border-stone-200 hover:border-[#C71E2E]/40"}`}>{h}h</button>
            ))}
          </div>
        </div>
        {recoveryRuns.length === 0 ? (
          <p className="mt-3 text-xs font-ui text-muted-foreground">No recovery runs recorded yet.</p>
        ) : (
          <div className="mt-4 space-y-2">
            {recoveryRuns.map((r, i) => (
              <div key={r.run_id} data-testid={`recovery-run-${i}`} className="flex flex-wrap items-center justify-between gap-2 text-sm font-ui border-b border-[#F5EFE6] pb-2 last:border-0">
                <span className="text-xs text-muted-foreground">{new Date(r.at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} · {r.delay_hours}h delay</span>
                <span className="text-xs text-muted-foreground">{r.nudged} nudged</span>
                <span className="text-xs text-muted-foreground">{r.recovered === null ? "—" : `${r.recovered} paid`}</span>
                <span className="text-xs font-semibold text-emerald-700">{r.recovered === null ? "untracked" : `${r.rate_pct}%`}</span>
                <span className="font-semibold text-[#1A1A1A]">{r.recovered === null ? "" : `$${Number(r.recovered_revenue).toFixed(2)}`}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {util && (
        <div data-testid="fleet-utilization-card" className={`${CARD} mt-6`}>
          <p className="font-ui text-xs tracking-[0.2em] uppercase text-[#4A4A4A] flex items-center gap-2">
            <Car size={14} className="text-[#C71E2E]" /> Fleet Utilization — {util.month}
          </p>
          <div className="mt-4 space-y-3">
            {util.vehicles.map((v) => (
              <div key={v.vehicle_id} data-testid={`util-row-${v.vehicle_id}`}>
                <div className="flex items-baseline justify-between gap-3 text-sm font-ui">
                  <span className="text-[#1A1A1A]">{v.name}</span>
                  <span className="text-xs text-muted-foreground">{v.bookings} booking{v.bookings === 1 ? "" : "s"} · {v.days_booked}d booked · <b className="text-[#1A1A1A]">{v.utilization_pct}%</b></span>
                </div>
                <div className="mt-1 h-1.5 rounded-full bg-black/10 overflow-hidden">
                  <div className="h-full rounded-full bg-[#C71E2E] transition-[width] duration-500" style={{ width: `${Math.min(100, v.utilization_pct)}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {stats.addons && (
        <div data-testid="addons-attach-card" className={`${CARD} mt-6`}>
          <p className="font-ui text-xs tracking-[0.2em] uppercase text-muted-foreground">Add-on attach rate</p>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[["SLI insurance", stats.addons.sli, "stats-addon-sli"], ["Roadside assistance", stats.addons.roadside, "stats-addon-roadside"], ["GPS unit", stats.addons.gps, "stats-addon-gps"], ["Baby seat", stats.addons.baby_seat, "stats-addon-babyseat"]].map(([label, a, tid]) => (
              <div key={tid} data-testid={tid} className="rounded-xl border border-[#E5DED5] bg-[#FAF6F0] px-4 py-3">
                <p className="text-xs font-ui text-muted-foreground">{label}</p>
                <p className="mt-1 font-ui text-lg font-semibold text-[#1A1A1A]">{stats.addons.rentals ? Math.round((a.count / stats.addons.rentals) * 100) : 0}% <span className="text-xs font-normal text-muted-foreground">of rentals</span></p>
                <p className="text-xs font-ui text-emerald-700">{a.count} sold · {fmtMoney(a.revenue)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {stats.funnel && (
        <div data-testid="funnel-card" className={`${CARD} mt-6`}>
          <p className="font-ui text-xs tracking-[0.2em] uppercase text-[#4A4A4A]">Rental Funnel & Nudge Performance</p>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <div className="flex items-baseline justify-between">
                <p className="text-sm font-ui text-[#1A1A1A]">Contracts signed before pickup</p>
                <p data-testid="funnel-contract-pct" className="font-ui text-2xl font-semibold text-[#1A1A1A]">{stats.funnel.contract_signed_pct}%</p>
              </div>
              <div className="mt-2 h-1.5 rounded-full bg-black/10 overflow-hidden">
                <div className="h-full rounded-full bg-[#C71E2E] transition-[width] duration-500" style={{ width: `${stats.funnel.contract_signed_pct}%` }} />
              </div>
              <p data-testid="funnel-contract-detail" className="mt-2 text-xs font-ui text-[#4A4A4A]">
                {stats.funnel.contract_signed} of {stats.funnel.contract_total} rentals · nudge conversions: {stats.funnel.contract_nudge_signed} of {stats.funnel.contract_nudge_total} nudged
              </p>
            </div>
            <div>
              <div className="flex items-baseline justify-between">
                <p className="text-sm font-ui text-[#1A1A1A]">Return photos submitted</p>
                <p data-testid="funnel-photos-pct" className="font-ui text-2xl font-semibold text-[#1A1A1A]">{stats.funnel.photos_submitted_pct}%</p>
              </div>
              <div className="mt-2 h-1.5 rounded-full bg-black/10 overflow-hidden">
                <div className="h-full rounded-full bg-[#C71E2E] transition-[width] duration-500" style={{ width: `${stats.funnel.photos_submitted_pct}%` }} />
              </div>
              <p data-testid="funnel-photos-detail" className="mt-2 text-xs font-ui text-[#4A4A4A]">
                {stats.funnel.photos_submitted} of {stats.funnel.photos_total} completed rentals · nudge conversions: {stats.funnel.photos_nudge_done} of {stats.funnel.photos_nudge_total} nudged
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div data-testid="revenue-month-card" className={`${CARD} flex flex-col justify-between gap-4`}>
          <div>
            <p className="font-ui text-xs tracking-[0.2em] uppercase text-[#4A4A4A] flex items-center gap-2">
              <DollarSign size={14} className="text-[#C71E2E]" /> Revenue This Month — {monthName}
            </p>
            <p data-testid="revenue-month-value" className="mt-3 font-display text-3xl tracking-tight text-[#1A1A1A]">
              {fmtMoney(revenue.thisMonth)}
            </p>
            <p data-testid="revenue-month-delta" className={`mt-1 text-sm font-ui flex items-center gap-1.5 ${revenue.delta === null ? "text-[#4A4A4A]" : revenue.delta >= 0 ? "text-emerald-600" : "text-[#C71E2E]"}`}>
              {revenue.delta === null
                ? "First tracked month"
                : <>{revenue.delta >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />} {revenue.delta >= 0 ? "↑" : "↓"} {Math.abs(Math.round(revenue.delta))}% vs last month</>}
            </p>
          </div>
          <p className="text-xs text-[#4A4A4A] font-ui max-w-xs leading-relaxed">
            Collected payments this month, minus refunds. {fmtMoney(revenue.lastMonth)} collected last month.
          </p>
        </div>

        <div data-testid="downtime-card" className={`${CARD} flex flex-col justify-between gap-4`}>
          <div>
            <p className="font-ui text-xs tracking-[0.2em] uppercase text-[#4A4A4A] flex items-center gap-2">
              <CalendarX2 size={14} className="text-[#C71E2E]" /> Downtime This Month — {monthName}
            </p>
            <p data-testid="downtime-days" className="mt-3 font-display text-3xl tracking-tight text-[#1A1A1A]">
              {downtime.vehicleDays} vehicle-days blocked
            </p>
            <p data-testid="downtime-lost" className="mt-1 text-sm text-[#4A4A4A] font-ui">
              ≈ <span className="text-[#C71E2E] font-semibold">{fmtMoney(downtime.lost)}</span> potential revenue lost to blackouts this month
            </p>
          </div>
          <p className="text-xs text-[#4A4A4A] font-ui max-w-xs leading-relaxed">
            Estimated from blackout ranges × daily rates. Fleet-wide blocks count every active vehicle.
          </p>
        </div>

        <div data-testid="admin-promo-card" className={CARD}>
          <p className="font-ui text-xs tracking-[0.2em] uppercase text-[#4A4A4A] flex items-center gap-2">
            <Megaphone size={14} className="text-[#C71E2E]" /> Promotion Banner
          </p>
          <p className="mt-2 text-xs text-[#4A4A4A]">Displayed at the top of every page when switched on.</p>
          <div className="mt-4 flex items-center gap-3">
            <Switch data-testid="promo-toggle" checked={promo.enabled} onCheckedChange={(v) => setPromo({ ...promo, enabled: v })} />
            <span className="text-sm font-ui text-[#1A1A1A]">{promo.enabled ? "Banner ON" : "Banner OFF"}</span>
          </div>
          <Input data-testid="promo-message-input" value={promo.message} onChange={(e) => setPromo({ ...promo, message: e.target.value })} placeholder="e.g. 15% off weekly rentals this month!" className="input-dark mt-4" />
          <Button data-testid="promo-save-button" onClick={savePromo} disabled={savingPromo} className="mt-4 rounded-full bg-[#C71E2E] text-white hover:bg-[#A01825] font-ui hover:scale-95 transition-transform">
            {savingPromo ? "Saving…" : "Save Banner"}
          </Button>
        </div>
      </div>

      <SearchPerfCard />

      <FraudMap />

      <div data-testid="maintenance-chart-card" className={`mt-6 ${CARD}`}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="font-ui text-xs tracking-[0.2em] uppercase text-[#4A4A4A] flex items-center gap-2">
            <Wrench size={14} className="text-[#C71E2E]" /> Fleet Upkeep — Monthly Spend
          </p>
          <p data-testid="maintenance-chart-total" className="text-sm font-ui text-[#4A4A4A]">
            6-month total: <span className="text-[#C71E2E] font-semibold">{fmtMoney(sixMonthTotal)}</span>
          </p>
        </div>
        <div className="mt-6 flex items-end gap-3 h-36">
          {months.map((m) => (
            <div key={m.key} data-testid={`maintenance-bar-${m.key}`} className="flex-1 flex flex-col items-center justify-end gap-2 h-full group">
              <p className="text-[11px] font-ui text-[#4A4A4A] opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                {fmtMoney(m.total)}
              </p>
              <div
                className={`w-full max-w-[56px] rounded-t-lg transition-all duration-300 ${m.total > 0 ? "bg-[#C71E2E]/70 group-hover:bg-[#C71E2E] group-hover:shadow-[0_0_18px_rgba(199,30,46,0.35)]" : "bg-[#E5DED5]"}`}
                style={{ height: `${Math.max((m.total / maxSpend) * 100, 4)}%` }}
                title={`${m.label}: ${fmtMoney(m.total)}`}
              />
              <p className="text-[11px] font-ui text-[#4A4A4A]">{m.label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
